import { Env } from '@env';
import { Slider } from '@miblanchard/react-native-slider';
import { Image } from 'expo-image';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import { useCustomer } from '@/api/customers/use-customer';
import { useCar } from '@/api/partners/cars/use-car';
import {
  trimsForVin,
  useCarDetailOptions,
} from '@/api/partners/cars/use-car-detail-options';
import { useCarSubscriptionEarnings } from '@/api/partners/cars/use-car-subscription-earnings';
import { useCarProjectedMonthlyIncomes } from '@/api/partners/cars/use-cars-projected-monthly-incomes';
import { useConnectStandardFleet } from '@/api/partners/cars/use-connect-standard-fleet';
import { useUpdateCar } from '@/api/partners/cars/use-update-car';
import { useCreateLocation } from '@/api/partners/locations/use-create-location';
import { useLocations } from '@/api/partners/locations/use-locations';
import BackButton from '@/components/back-button';
import type { AddLocationFormProps } from '@/components/cars/add-location-form';
import AddLocationForm from '@/components/cars/add-location-form';
import { HowAutomatedPricingWorksModal } from '@/components/cars/bulk-list/how-automated-pricing-works-modal';
import type { AvailabilityFormType } from '@/components/cars/setup/availability-form';
import AvailabilityForm from '@/components/cars/setup/availability-form';
import type { CarSetupFormType } from '@/components/cars/setup/car-setup-form';
import CarSetupForm from '@/components/cars/setup/car-setup-form';
import { WhyGloveboxPinModal } from '@/components/cars/setup/why-glovebox-pin-modal';
import { SubscriptionEarningsCard } from '@/components/edit-car/subscription-earnings-card';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import {
  formatNumber,
  getCarImages,
  isDesktop,
  snakeToCamelCase,
  snakeToTitleCase,
} from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { usePoll } from '@/core/hooks/use-poll';
import { useTrackMetaEvent } from '@/core/hooks/use-track-meta-event';
import { clearByPrefix, usePersistedState } from '@/core/storage';
import { GREY_10, GREY_30, GREY_60, PINKLING } from '@/style/colors';
import { Button, Card, Input, showErrorMessage, Text } from '@/ui';
import { CheckItem } from '@/ui/check-item';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';
import { Icon } from '@/ui/icon';
import { Car as CarIcon, Lock, Search } from '@/ui/icons';
import { ChevronLeft } from '@/ui/icons/chevron-left';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { ClockBadgeCheckmark } from '@/ui/icons/clock-badge-checkmark';
import { Dollar } from '@/ui/icons/dollar';
import { ShieldCheckmark } from '@/ui/icons/shield-checkmark';
import { ShieldPerson } from '@/ui/icons/shield-person';
import { ListItem } from '@/ui/list-item';
import { Loader } from '@/ui/loader';
import { ProgressIndicator } from '@/ui/progress-indicator';
import { Radio } from '@/ui/radio';
import Toggle from '@/ui/toggle';

function interpolate(data: Record<number, number>, x: number): number | null {
  const xs = Object.keys(data)
    .map(Number)
    .sort((a, b) => a - b);

  for (let i = 0; i < xs.length - 1; i++) {
    const x1 = xs[i];
    const x2 = xs[i + 1];

    if (x >= x1 && x <= x2) {
      const y1 = data[x1];
      const y2 = data[x2];

      return y1 + (y2 - y1) * ((x - x1) / (x2 - x1));
    }
  }

  return null;
}

enum Step {
  CHOOSE_CAR = 0,
  ADD_A_LOCATION = 1,
  FILL_OUT_MISSING_DETAILS = 2,
  PRICING = 3,
  AVAILABILITY = 4,
  SUBSCRIPTION = 5,
}

const Steps = [
  {
    step: Step.CHOOSE_CAR,
    title: 'Select and List Your First Car',
    caption: 'Choose your first car. You can add more cars later',
  },
  {
    step: Step.ADD_A_LOCATION,
    title: 'Add Location',
  },
  {
    step: Step.FILL_OUT_MISSING_DETAILS,
    title: 'Car Setup',
  },
  {
    step: Step.PRICING,
    title: 'Set Your Pricing',
  },
  {
    step: Step.AVAILABILITY,
    title: 'Set Availability',
  },
  {
    step: Step.SUBSCRIPTION,
    title: 'Subscription',
  },
];

export default function Setup() {
  const { styles, theme } = useStyles(stylesheet);
  const local = useLocalSearchParams();
  const router = useRouter();
  const trackMetaEvent = useTrackMetaEvent();
  const { data: customer } = useCustomer();
  const submittedRef = useRef(false);

  // Draft prefix for this setup flow. Flow-level state (step pointer, which
  // car is selected) is stored flat under this prefix. Per-car form drafts
  // are stored as `Record<carId, T>` maps under their own keys below — same
  // shape bulk-list uses — so every field lives in a single MMKV slot keyed
  // by car id rather than one slot per (car, field) pair.
  const draftPrefix = `owner-setup-draft:${String(local.id ?? 'new')}:`;
  const k = (name: string) => `${draftPrefix}${name}`;

  const [filterText, setFilterText] = useState('');
  const [currentStep, setCurrentStep] = usePersistedState(
    k('currentStep'),
    Step.CHOOSE_CAR
  );
  const [cars, setCars] = useState<Car[]>([]);
  const [selectedCar, setSelectedCar] = usePersistedState<Car | null>(
    k('selectedCar'),
    null
  );

  // Car id used to index every per-car draft map below. For an existing
  // listing it's the route's car id; for the "new" flow it's whichever car
  // the user picked on the first step.
  const routeCarId = Array.isArray(local.id) ? local.id[0] : local.id;
  const carScopeId: number =
    routeCarId != null ? Number(routeCarId) : selectedCar?.id ?? -1;

  const [carSetupFormValuesByCar, setCarSetupFormValuesByCar] =
    usePersistedState<Record<number, CarSetupFormType | undefined>>(
      k('carSetupFormValues'),
      {}
    );
  const carSetupFormValues = carSetupFormValuesByCar[carScopeId];
  const [carSetupFormValid, setCarSetupFormValid] = useState<boolean>(false);

  const [addLocationFormValuesByCar, setAddLocationFormValuesByCar] =
    usePersistedState<Record<number, AddLocationFormProps | undefined>>(
      k('addLocationFormValues'),
      {}
    );
  const addLocationFormValues = addLocationFormValuesByCar[carScopeId];

  const [showAddLocationFormByCar, setShowAddLocationFormByCar] =
    usePersistedState<Record<number, boolean>>(k('showAddLocationForm'), {});
  const showAddLocationForm = showAddLocationFormByCar[carScopeId] ?? true;

  const [addLocationFormValid, setAddLocationFormValid] =
    useState<boolean>(false);

  const [pickupLocationByCar, setPickupLocationByCar] = usePersistedState<
    Record<number, DropdownOption | null>
  >(k('pickupLocation'), {});
  const pickupLocation = pickupLocationByCar[carScopeId] ?? null;

  const [showWhyGloveboxPinModal, setShowWhyGloveboxPinModal] = useState(false);
  const [
    showHowAutomatedPricingWorksModal,
    setShowHowAutomatedPricingWorksModal,
  ] = useState(false);

  const [enabledFixedRateByCar, setEnabledFixedRateByCar] = usePersistedState<
    Record<number, boolean>
  >(k('enabledFixedRate'), {});
  const enabledFixedRate = enabledFixedRateByCar[carScopeId] ?? false;

  const [minRateByCar, setMinRateByCar] = usePersistedState<
    Record<number, number>
  >(k('minRate'), {});
  const minRate = minRateByCar[carScopeId] ?? 0;

  const [dailyRateByCar, setDailyRateByCar] = usePersistedState<
    Record<number, number>
  >(k('dailyRate'), {});
  const dailyRate = dailyRateByCar[carScopeId] ?? 100;

  const [availabilityFormValid, setAvailabilityFormValid] = useState(false);

  const [availabilityFormValuesByCar, setAvailabilityFormValuesByCar] =
    usePersistedState<Record<number, AvailabilityFormType | undefined>>(
      k('availabilityFormValues'),
      {}
    );
  const availabilityFormValues = availabilityFormValuesByCar[carScopeId];

  const [allowSubscriptionByCar, setAllowSubscriptionByCar] = usePersistedState<
    Record<number, boolean>
  >(k('allowSubscription'), {});
  const allowSubscription = allowSubscriptionByCar[carScopeId] ?? true;
  const scrollRef = useRef<KeyboardAwareScrollView>(null);

  const {
    data: carData,
    isFetching: isFetchingCar,
    refetch: refetchCar,
  } = useCar({
    variables: {
      id: Number(local.id),
    },
    enabled: !!local.id,
  });

  const { data: locationsData, refetch: refetchLocations } = useLocations();

  const { mutateAsync: createLocation, isPending: isCreatingLocation } =
    useCreateLocation();

  const {
    mutateAsync: connectStandardFleet,
    isPending: isConnectingStandardFleet,
  } = useConnectStandardFleet();

  const { isPolling: isPollingStandardFleet, poll: pollConnectStandardFleet } =
    usePoll<Car[]>(
      () => connectStandardFleet({}),
      (data) => data.length > 0
    );

  const { mutateAsync: updateCar, isPending: isUpdatingCar } = useUpdateCar();

  const {
    data: carDetailOptions,
    isLoading: isLoadingCarDetailOptions,
    refetch: refetchCarDetailOptions,
  } = useCarDetailOptions({
    variables: {
      vins: cars
        .filter((car) => car.id === selectedCar?.id)
        .map((car) => car.vin),
    },
    enabled: currentStep >= Step.ADD_A_LOCATION && cars.length > 0,
  });

  const {
    data: carSubscriptionEarnings,
    isPending: isLoadingCarSubscriptionEarnings,
  } = useCarSubscriptionEarnings({
    variables: {
      id: selectedCar?.id,
      defaultLocationId: pickupLocation ? Number(pickupLocation.id) : undefined,
      trimId: carSetupFormValues?.trimId
        ? Number(carSetupFormValues.trimId)
        : undefined,
    },
    enabled: currentStep === Step.SUBSCRIPTION && selectedCar !== null,
  });

  const {
    data: projectedMonthlyIncomes,
    isPending: isLoadingProjectedMonthlyIncomes,
  } = useCarProjectedMonthlyIncomes({
    variables: {
      vins: [selectedCar?.vin],
    },
    enabled: currentStep === Step.PRICING && selectedCar !== null,
  });

  useEffect(() => {
    if (!local.id) {
      handleConnectStandardFleet();
    }
  }, [local.id]);

  useEffect(() => {
    if (local.id) {
      mixpanelService.track('Listing Started', {
        'Step Name': 'List Car',
      });
    } else {
      mixpanelService.track('Listing Started', {
        'Step Name': 'Select First Car',
      });
    }
  }, [local.id]);

  useEffect(() => {
    // Wait for the customer to load before firing: this event auto-fires on
    // mount after the SF redirect, and trackMetaEvent needs the customer
    // present to attach Advanced Matching to the Pixel event (otherwise em/fn/ln
    // get dropped). Gating on `customer` guarantees the PII is available.
    if (local.fromSf && customer && !submittedRef.current) {
      submittedRef.current = true;
      mixpanelService.track('Connecting SF Succeeded');
      // Mirror the Mixpanel event to Meta Pixel + CAPI (deduped).
      trackMetaEvent('CompleteRegistration');
    }
  }, [local.fromSf, customer, trackMetaEvent]);

  useEffect(() => {
    if (local.id && carData) {
      setCars([carData]);
      setSelectedCar((prev) => prev ?? carData);
    }
  }, [carData, local.id]);

  useEffect(() => {
    scrollRef.current?.scrollToPosition(0, 0, false);
  }, [currentStep]);

  const selectedStep = useMemo(() => {
    return Steps.find((step) => step.step === currentStep);
  }, [currentStep]);

  const projectedMonthlyIncome = useMemo(() => {
    if (!projectedMonthlyIncomes || !selectedCar) return [];

    return Object.values(projectedMonthlyIncomes)[0];
  }, [projectedMonthlyIncomes]);

  useEffect(() => {
    if (projectedMonthlyIncome && !minRate) {
      setMinRateByCar((prev) => ({
        ...prev,
        [carScopeId]: projectedMonthlyIncome.defaultRate,
      }));
    }
  }, [projectedMonthlyIncome, minRate, carScopeId, setMinRateByCar]);

  const histogramData = useMemo(() => {
    if (!projectedMonthlyIncomes || !selectedCar) return [];

    const rateToPercentBooked =
      projectedMonthlyIncomes[snakeToCamelCase(selectedCar.model.name)]
        .rateToPercentBooked;

    return Object.keys(rateToPercentBooked).map((rate) => {
      const percentBooked = rateToPercentBooked[rate];

      return {
        x: Number(rate),
        y: percentBooked,
      };
    });
  }, [selectedCar, projectedMonthlyIncomes]);

  const handleSelectCar = (car: Car) => {
    // Each draft field is a `Record<carId, T>` map keyed by `carScopeId`, so
    // switching cars just re-selects that car's slice — no explicit reset is
    // needed.
    setSelectedCar(car);
  };

  const handleAddLocationFormChange = (data: AddLocationFormProps) => {
    setAddLocationFormValuesByCar((prev) => ({ ...prev, [carScopeId]: data }));
  };

  const handleAddLocationValidChange = (isValid: boolean) => {
    setAddLocationFormValid(isValid);
  };

  const handleCarSetupFormChange = useCallback(
    (values: CarSetupFormType) => {
      setCarSetupFormValuesByCar((prev) => ({
        ...prev,
        [carScopeId]: values,
      }));
    },
    [carScopeId]
  );

  const handleCarSetupFormValidChange = useCallback((isValid: boolean) => {
    setCarSetupFormValid(isValid);
  }, []);

  const handleConnectStandardFleet = async () => {
    try {
      const connectedCars = await pollConnectStandardFleet();

      const filteredCars = (connectedCars ?? []).filter((car: Car) => {
        return !car.isApproved;
      });

      setCars(filteredCars);
      setSelectedCar((prev) => prev ?? filteredCars[0] ?? null);
    } catch (error: any) {
      showErrorMessage(error.message);
    }
  };

  const getTimeZone = async ({ lat, lng }: any) => {
    const now = Date.now() / 1000;
    const key = Env.GOOGLE_TIMEZONE_API_KEY;

    const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat}%2C${lng}&timestamp=${now}&key=${key}`;

    const res = await fetch(url, {
      method: 'GET',
    });
    const d = await res.json();

    return d;
  };

  const handleNext = async () => {
    if (currentStep === Step.CHOOSE_CAR) {
      setFilterText('');

      if (locationsData && locationsData.length > 0) {
        setShowAddLocationFormByCar((prev) => ({
          ...prev,
          [carScopeId]: false,
        }));
      }

      mixpanelService.track('Listing Step Completed', {
        'Car VIN': selectedCar?.vin,
        'Step Name': local.id ? 'List Car' : 'Select First Car',
      });

      setCurrentStep(Step.ADD_A_LOCATION);
      return;
    }

    if (currentStep === Step.ADD_A_LOCATION) {
      mixpanelService.track('Listing Step Completed', {
        'Car VIN': selectedCar?.vin,
        'Step Name': 'Add Location',
      });

      if (!showAddLocationForm && pickupLocation) {
        setShowAddLocationFormByCar((prev) => ({
          ...prev,
          [carScopeId]: false,
        }));
        setCurrentStep(Step.FILL_OUT_MISSING_DETAILS);
        await refetchCarDetailOptions();
        return;
      }

      const {
        address: addr,
        pickupInstructions,
        dropoffInstructions,
        garageOpenHour,
        garageCloseHour,
        enableGarageHours,
      } = addLocationFormValues!;

      const timeZone = await getTimeZone({
        lat: addr.latitude,
        lng: addr.longitude,
      });

      try {
        const loc = await createLocation({
          address: addr.description,
          zipcode: addr.zipcode,
          city: addr.city,
          state: addr.state,
          latitude: addr.latitude,
          longitude: addr.longitude,
          pickupInstructions,
          dropoffInstructions,
          timeZone: timeZone.timeZoneId,
          garageOpenHour: enableGarageHours ? garageOpenHour : undefined,
          garageCloseHour: enableGarageHours ? garageCloseHour : undefined,
        });

        Toast.show({
          text1: `${addr.description} has been added`,
          type: 'success',
        });
        // Pickup Location dropdown options come from
        // `carDetailOptions.pickupLocations`, so refresh that too — otherwise
        // the newly-created location won't appear in the dropdown when the
        // user navigates back to this step.
        await Promise.all([refetchLocations(), refetchCarDetailOptions()]);

        setPickupLocationByCar((prev) => ({
          ...prev,
          [carScopeId]: {
            id: String(loc.data.id),
            value: String(loc.data.id),
            title: addr.description,
          },
        }));

        setShowAddLocationFormByCar((prev) => ({
          ...prev,
          [carScopeId]: false,
        }));

        setCurrentStep(Step.FILL_OUT_MISSING_DETAILS);
      } catch (error: any) {
        showErrorMessage(error.message);
      }
      return;
    }

    if (currentStep === Step.FILL_OUT_MISSING_DETAILS) {
      mixpanelService.track('Listing Step Completed', {
        'Car VIN': selectedCar?.vin,
        'Step Name': 'Car Setup',
      });
      setCurrentStep(Step.PRICING);
    }

    if (currentStep === Step.PRICING) {
      mixpanelService.track('Listing Step Completed', {
        'Car VIN': selectedCar?.vin,
        'Step Name': 'Pricing',
      });
      setCurrentStep(Step.AVAILABILITY);
    }

    if (currentStep === Step.AVAILABILITY) {
      mixpanelService.track('Listing Step Completed', {
        'Car VIN': selectedCar?.vin,
        'Step Name': 'Availability',
      });
      setCurrentStep(Step.SUBSCRIPTION);
    }

    if (currentStep === Step.SUBSCRIPTION) {
      try {
        await updateCar({
          id: selectedCar!.id,
          defaultLocationId: parseInt(pickupLocation!.id, 10),
          trimId: carSetupFormValues!.trimId,
          color: Object.entries(carDetailOptions.colors)
            .map(
              ([color, idx]) =>
                ({
                  id: String(idx),
                  value: color,
                  title:
                    color.charAt(0).toUpperCase() +
                    color.slice(1).replace(/([A-Z])/g, ' $1'),
                } as DropdownOption)
            )
            .find((option) => option.value === carSetupFormValues!.color!)?.id,
          numSeats: carSetupFormValues!.numSeats!,
          licensePlate: carSetupFormValues!.licensePlate,
          licensePlateState: Object.entries(carDetailOptions.licensePlateStates)
            .map(
              ([state, idx]) =>
                ({
                  id: String(idx),
                  value: state,
                  title:
                    state.charAt(0).toUpperCase() +
                    state.slice(1).replace(/([A-Z])/g, ' $1'),
                } as DropdownOption)
            )
            .find(
              (option) =>
                option.value === carSetupFormValues!.licensePlateState!
            )?.id,
          gloveboxPin: carSetupFormValues!.gloveboxPin,
          useAutomatedPricing: !enabledFixedRate,
          dailyRate: dailyRate.toString(),
          minimumDailyRate: !enabledFixedRate ? minRate.toString() : undefined,
          maximumDailyRate: !enabledFixedRate ? '5000' : undefined,
          minDaysToBook: String(availabilityFormValues!.minDaysToBook),
          hoursBetweenTrips: String(availabilityFormValues!.hoursBetweenTrips),
          minHoursInAdvance: String(availabilityFormValues!.minHoursInAdvance),
          supportsDelivery: availabilityFormValues!.supportsDelivery,
          maxDeliveryDistance: availabilityFormValues!.maxDeliveryDistance,
          deliveryPersonPhone: availabilityFormValues!.deliveryPersonPhone,
          allowSubscription: allowSubscription,
        });

        mixpanelService.track('Listing Step Completed', {
          'Car VIN': selectedCar?.vin,
          'Step Name': 'Subscription',
        });

        mixpanelService.track('Listing Completed', {
          'Car VIN': selectedCar?.vin,
          'Car Brand': selectedCar?.brandName,
          'Car Model': selectedCar?.modelName,
          'Car Year': selectedCar?.vehicleYear,
          'Daily Rate': enabledFixedRate ? dailyRate : '',
          'Minimum Rate': !enabledFixedRate ? minRate : '',
          'Subscription Available': allowSubscription,
          'Delivery Available': availabilityFormValues!.supportsDelivery,
        });

        clearByPrefix(draftPrefix);

        router.replace({
          pathname: '/partner/cars',
          params: {
            showCarListedModal: 'true',
            showCarListedModalCarId: selectedCar?.id,
          },
        });
      } catch (e: any) {
        showErrorMessage(e.message);
      }
    }
  };

  const renderBody = () => {
    if (currentStep === Step.CHOOSE_CAR) {
      return (
        <View style={{ gap: 8 }}>
          {cars
            .filter((car) => {
              if (!filterText) return true;

              if (car.vin?.toLowerCase().includes(filterText.toLowerCase()))
                return true;
              if (
                car.modelName?.toLowerCase().includes(filterText.toLowerCase())
              )
                return true;
              if (
                car.brandName?.toLowerCase().includes(filterText.toLowerCase())
              )
                return true;

              return false;
            })
            .map((car) => {
              return (
                <View
                  key={car.id}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Radio
                    onPress={() => {
                      handleSelectCar(car);
                    }}
                    active={selectedCar?.id === car.id}
                  />

                  <Image
                    style={{
                      width: 100,
                      height: 42,
                    }}
                    source={{
                      uri:
                        car && getCarImages(car.images).length > 0
                          ? getCarImages(car.images)[0].url
                          : '',
                    }}
                    contentFit="cover"
                  />

                  <View>
                    <Text type="bodyRegular">
                      {car.vehicleYear && car.brandName && car.modelName
                        ? `${car.vehicleYear} ${car.brandName} ${car.modelName}`
                        : 'Loading Car Information...'}
                    </Text>

                    <Text
                      type="caption1"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {car.vin}
                    </Text>
                  </View>
                </View>
              );
            })}
        </View>
      );
    }

    if (currentStep === Step.ADD_A_LOCATION) {
      return (
        <View>
          {!showAddLocationForm ? (
            <View style={{ gap: 16 }}>
              <Dropdown
                mode="single"
                label="Pickup Location"
                options={(carDetailOptions?.pickupLocations ?? []).map(
                  (loc) => {
                    return {
                      id: String(loc.id),
                      value: String(loc.id),
                      title: loc.address,
                    } as DropdownOption;
                  }
                )}
                placeHolderText="Select your car's pickup location"
                onSelect={(selected: DropdownOption[]) => {
                  setPickupLocationByCar((prev) => ({
                    ...prev,
                    [carScopeId]: selected[0],
                  }));
                }}
                onClear={() => {
                  setPickupLocationByCar((prev) => ({
                    ...prev,
                    [carScopeId]: null,
                  }));
                }}
                values={pickupLocation ? [pickupLocation.id] : []}
                style={styles.pickupLocationDropdown}
                error={
                  !pickupLocation ? 'Pickup location is required' : undefined
                }
              />

              <Button
                text="Add new location"
                type="secondaryExtraSmall"
                onPress={() => {
                  setShowAddLocationFormByCar((prev) => ({
                    ...prev,
                    [carScopeId]: true,
                  }));
                }}
                style={{ width: 150 }}
                loading={isUpdatingCar}
              />
            </View>
          ) : null}

          {showAddLocationForm ? (
            <AddLocationForm
              onChange={handleAddLocationFormChange}
              onValidChange={handleAddLocationValidChange}
              defaultValues={addLocationFormValues}
            />
          ) : null}
        </View>
      );
    }

    if (
      currentStep === Step.FILL_OUT_MISSING_DETAILS &&
      carDetailOptions &&
      selectedCar
    ) {
      const resolvedTrims = trimsForVin({
        vin: selectedCar.vin,
        trims: carDetailOptions.trims,
        vinTrims: carDetailOptions.vinTrims,
        modelId: selectedCar.model?.id,
      });

      return (
        <View style={styles.carDetailsFormContainer}>
          <CarSetupForm
            key={selectedCar.id}
            trimOptions={resolvedTrims.trims.map((trim) => {
              return {
                id: String(trim.id),
                value: String(trim.id),
                title: snakeToTitleCase(trim.name),
              } as DropdownOption;
            })}
            trimLocked={resolvedTrims.locked}
            licensePlateStateOptions={Object.entries(
              carDetailOptions.licensePlateStates
            ).map(
              ([state, idx]) =>
                ({
                  id: String(idx),
                  value: state,
                  title:
                    state.charAt(0).toUpperCase() +
                    state.slice(1).replace(/([A-Z])/g, ' $1'),
                } as DropdownOption)
            )}
            colorOptions={Object.entries(carDetailOptions.colors).map(
              ([color, idx]) =>
                ({
                  id: String(idx),
                  value: color,
                  title:
                    color.charAt(0).toUpperCase() +
                    color.slice(1).replace(/([A-Z])/g, ' $1'),
                } as DropdownOption)
            )}
            car={selectedCar}
            onChange={handleCarSetupFormChange}
            onValidChange={handleCarSetupFormValidChange}
            style={styles.carDetailsForm}
            defaultValues={carSetupFormValues}
          />

          <Button
            text="What Is Glovebox Pin and Why Do I Need It?"
            type="ghost"
            onPress={() => {
              setShowWhyGloveboxPinModal(true);
            }}
            style={styles.gloveboxPinButton}
            textStyle={{
              textDecorationLine: 'underline',
            }}
          />
        </View>
      );
    }

    if (currentStep === Step.PRICING && selectedCar) {
      return (
        <View style={styles.pricingContainer}>
          <View>
            <Text type="title2">{`${selectedCar.brandName} ${selectedCar.modelName}`}</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Booking frequency by daily rate
            </Text>
          </View>

          {/* {histogramData?.length > 0 ? (
            <BarChart
              data={Array.from({ length: 4 }, (_, i) => ({
                x: i,
                y: 40 + 30 * Math.random(),
              }))}
              minValue={20} // for conditional coloring
              barWidth={40} // optional, default is 40
              xAxisLabel="Daily Price"
              yAxisLabel="Booking Volume"
              theme={theme}
              width={'100%'}
              height={250}
              tickFormatter={(x) =>
                projectedMonthlyIncome?.maximumRate === x ? `$${x}+` : `$${x}`
              }
            />

          ) : null} */}

          {/* <Image
            style={{
              width: '100%',
              height: 300,
            }}
            source={getPricingHistogram(selectedCar.model.name)}
            contentFit="contain"
          /> */}

          <View>
            <Card style={{ padding: 16, gap: 24 }}>
              <View style={{ gap: 8 }}>
                {enabledFixedRate ? (
                  <View style={{ gap: 8 }}>
                    <Text type="title2">Set Your Fixed Daily Rate</Text>

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      Your car will always be listed at exactly the price you
                      set. Guests pay a consistent, predictable rate every day.
                      No dynamic adjustments
                    </Text>
                  </View>
                ) : (
                  <View style={{ gap: 8 }}>
                    <Text type="title2">Set your daily rates</Text>

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      Set your minimum and maximum daily rates. Our pricing AI
                      adjusts prices within this range to help maximize revenue.
                      The chart shows how often Model 3 vehicles are booked at
                      different price points in the market, based on historical
                      data. Your real world earnings will vary based on
                      availability, your cars trim, year, demand, and
                      seasonality.
                    </Text>
                  </View>
                )}

                <Button
                  text="How Does Pricing Work?"
                  type="primarySmall"
                  onPress={() => {
                    setShowHowAutomatedPricingWorksModal(true);
                  }}
                  style={{
                    width: 230,
                  }}
                />
              </View>

              <View style={{ flexDirection: 'row', gap: 16 }}>
                {!enabledFixedRate ? (
                  <View
                    style={{
                      flex: 1,
                      padding: 16,
                      gap: 4,
                      borderWidth: 1,
                      borderColor: theme.colors.card.default.borderColor,
                      borderRadius: 16,
                    }}
                  >
                    <Text type="caption1">Projected monthly income</Text>

                    {minRate ? (
                      <Text type="title1">
                        $
                        {Math.round(
                          interpolate(
                            projectedMonthlyIncome.rateToEarnings,
                            minRate?.toString()
                          )
                        )}
                      </Text>
                    ) : null}
                  </View>
                ) : null}

                {enabledFixedRate ? (
                  <View
                    style={{
                      flex: 1,
                      padding: 16,
                      gap: 4,
                      borderWidth: 1,
                      borderColor: theme.colors.card.default.borderColor,
                      borderRadius: 16,
                    }}
                  >
                    <Text type="caption1">Your take-home rate</Text>

                    <Text type="title1">
                      ${formatNumber(dailyRate * 0.7)}/Day
                    </Text>

                    <Text
                      type="caption1"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      70% of daily rate
                    </Text>
                  </View>
                ) : null}
              </View>

              <View
                style={{
                  padding: 16,
                  gap: 4,
                  backgroundColor: theme.colors.card.hover.backgroundColor,
                  borderWidth: 1,
                  borderColor: theme.colors.card.hover.borderColor,
                  borderRadius: 16,
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <View>
                  <Text type="bodyRegular">Fixed daily rate</Text>

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Lock in your price; it will remain static
                  </Text>
                </View>

                <Toggle
                  enabled={enabledFixedRate}
                  onToggle={() =>
                    setEnabledFixedRateByCar((prev) => ({
                      ...prev,
                      [carScopeId]: !enabledFixedRate,
                    }))
                  }
                />
              </View>

              {enabledFixedRate ? (
                <View style={{ gap: 16 }}>
                  <Text>Set your fixed daily price</Text>

                  <View style={styles.dailyRateInputContainer}>
                    <Input
                      value={dailyRate.toString()}
                      onChangeText={(t: string) =>
                        setDailyRateByCar((prev) => ({
                          ...prev,
                          [carScopeId]: Number(t),
                        }))
                      }
                      containerStyle={{ flex: 1 }}
                    />

                    <View
                      style={{
                        justifyContent: 'center',
                        gap: 4,
                      }}
                    >
                      <Text type="caption1">Per day</Text>

                      <Text
                        type="caption1"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        Your car will always be listed at this price
                      </Text>
                    </View>
                  </View>
                </View>
              ) : (
                <View style={{ gap: 16, marginBottom: 16 }}>
                  <Text
                    type="bodyRegular"
                    style={{
                      marginBottom: 16,
                    }}
                  >
                    Adjust your minimum daily rate
                  </Text>

                  <Slider
                    value={minRate ? Number(minRate) : 0}
                    onSlidingComplete={(values) => {
                      setMinRateByCar((prev) => ({
                        ...prev,
                        [carScopeId]: values[0],
                      }));
                    }}
                    onValueChange={(values) => {
                      setMinRateByCar((prev) => ({
                        ...prev,
                        [carScopeId]: values[0],
                      }));
                    }}
                    minimumValue={projectedMonthlyIncome.minimumRate}
                    maximumValue={projectedMonthlyIncome.maximumRate}
                    step={1}
                    minimumTrackTintColor={PINKLING}
                    maximumTrackTintColor={
                      UnistylesRuntime.themeName === 'dark' ? GREY_60 : GREY_30
                    }
                    thumbTintColor={
                      UnistylesRuntime.themeName === 'dark' ? GREY_10 : GREY_60
                    }
                    renderThumbComponent={(idx: number) => {
                      return (
                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: theme.colors.toggle.default.handle,
                            borderRadius: 16,
                            padding: 8,
                            width: 50,
                            borderColor: theme.colors.card.default.borderColor,
                            borderWidth: 1,
                          }}
                        >
                          <Icon
                            type="naked"
                            icon={<ChevronLeft />}
                            fillColor={theme.colors.icon.color}
                            width={10}
                            height={10}
                          />

                          <Icon
                            type="naked"
                            icon={<ChevronRight />}
                            fillColor={theme.colors.icon.color}
                            width={10}
                            height={10}
                          />
                        </View>
                      );
                    }}
                    renderAboveThumbComponent={() => {
                      return (
                        <View
                          style={{
                            flexDirection: 'row',
                            gap: 4,
                            position: 'absolute',
                            left: -40,
                            top: -20,
                          }}
                        >
                          <Text
                            type="bodyRegular"
                            style={{ color: theme.colors.text.secondary }}
                          >
                            Min
                          </Text>

                          <Text type="bodyRegular">
                            {`$${minRate}${
                              minRate === projectedMonthlyIncome.maximumRate
                                ? ' +'
                                : ''
                            }/day`}
                          </Text>
                        </View>
                      );
                    }}
                    trackMarks={[projectedMonthlyIncome.defaultRate]}
                    renderTrackMarkComponent={(idx: number) => {
                      return (
                        <View>
                          <Text
                            type="caption1"
                            style={{
                              position: 'absolute',
                              width: 200,
                              bottom: -30,
                              left: -40,
                              color: theme.colors.text.secondary,
                            }}
                          >
                            Recommended minimum
                          </Text>

                          <View
                            style={{
                              width: 16,
                              height: 16,
                              backgroundColor:
                                theme.colors.card.default.borderColor,
                              borderWidth: 1,
                              borderColor: theme.colors.icon.brandColor,
                              borderRadius: 8,
                            }}
                          />
                        </View>
                      );
                    }}
                  />
                </View>
              )}
            </Card>
          </View>

          <View style={{ gap: 16 }}>
            <Text type="title2">What's Included With Every Rental</Text>

            <ListItem
              text="Full Insurance Coverage"
              caption="Comprehensive protection with collision and up to the full value of your car and up to $1,000,000 in liability coverage included"
              icon={
                <Icon
                  type="dressed"
                  icon={<ShieldCheckmark />}
                  fillColor={theme.colors.icon.color}
                  width={25}
                  height={25}
                />
              }
              onPress={() => {}}
              withBorder={false}
            />

            <ListItem
              text="No Contract, No Lock-In"
              caption="Eon fills in the gaps between your existing listings so your cars can earn more without impacting your other revenue streams"
              icon={
                <Icon
                  type="dressed"
                  icon={<CarIcon />}
                  fillColor={theme.colors.icon.color}
                  width={25}
                  height={25}
                />
              }
              onPress={() => {}}
              withBorder={false}
            />

            <ListItem
              text="Guest Vetting & Onboarding"
              caption="We thoroughly screen and educate guests about the car and the entire process with Eon so pickups and drop offs happen automatically"
              icon={
                <Icon
                  type="dressed"
                  icon={<ShieldPerson />}
                  fillColor={theme.colors.icon.color}
                  width={25}
                  height={25}
                />
              }
              onPress={() => {}}
              withBorder={false}
            />

            <ListItem
              text="Automated Reimbursements"
              caption="Your additional miles, tolls and supercharging are automatically reimbursed with every rental"
              icon={
                <Icon
                  type="dressed"
                  icon={<Dollar />}
                  fillColor={theme.colors.icon.color}
                  width={25}
                  height={25}
                />
              }
              onPress={() => {}}
              withBorder={false}
            />

            <ListItem
              text="24/7 Customer Support"
              caption="Help available anytime, day or night for you and your guests"
              icon={
                <Icon
                  type="dressed"
                  icon={<ClockBadgeCheckmark />}
                  fillColor={theme.colors.icon.color}
                  width={25}
                  height={25}
                />
              }
              onPress={() => {}}
              withBorder={false}
            />

            <ListItem
              text="Chargeback & Tickets Protection"
              caption="Get fully reimbursed and paid out even if guests get a parking ticket or disputes a rental charge"
              icon={
                <Icon
                  type="dressed"
                  icon={<Lock />}
                  fillColor={theme.colors.icon.color}
                  width={25}
                  height={25}
                />
              }
              onPress={() => {}}
              withBorder={false}
            />
          </View>
        </View>
      );
    }

    if (currentStep === Step.AVAILABILITY && selectedCar && pickupLocation) {
      return (
        <View>
          <AvailabilityForm
            key={selectedCar.id}
            car={selectedCar}
            onChange={(values) => {
              setAvailabilityFormValuesByCar((prev) => ({
                ...prev,
                [carScopeId]: values,
              }));
            }}
            onValidChange={(isValid) => {
              setAvailabilityFormValid(isValid);
            }}
            deliveryDistanceCostOptions={
              carDetailOptions?.deliveryDistanceCostOptions
            }
            pickupLocation={
              locationsData?.find(
                (location) => String(location.id) === String(pickupLocation?.id)
              ) || null
            }
            defaultValues={availabilityFormValues}
          />
        </View>
      );
    }

    if (currentStep === Step.SUBSCRIPTION && selectedCar) {
      return (
        <View style={styles.subscriptionContainer}>
          <View>
            <CheckItem
              text="Allow Subscriptions"
              caption="Enable this to allow guests to subscribe to your car for extended periods. Weekly, monthly, or up to 6 months. Ideal for long-term rentals and recurring revenue."
              onPress={() => {
                setAllowSubscriptionByCar((prev) => ({
                  ...prev,
                  [carScopeId]: !allowSubscription,
                }));
              }}
              active={allowSubscription}
              type="checkbox"
            />
          </View>

          <SubscriptionEarningsCard earnings={carSubscriptionEarnings} />
        </View>
      );
    }

    return <View />;
  };

  if (
    isConnectingStandardFleet ||
    isPollingStandardFleet ||
    isLoadingCarDetailOptions ||
    (currentStep === Step.PRICING && isLoadingProjectedMonthlyIncomes)
  ) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  if (currentStep === Step.CHOOSE_CAR && local.id) {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />

        <ScrollView
          style={styles.listCarScrollView}
          contentContainerStyle={styles.listCarScrollViewContentContainer}
        >
          <View style={{ alignItems: 'center', gap: 24 }}>
            <View style={{ alignItems: 'center', gap: 8 }}>
              <Text type="title1">List Your Car</Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >{`${carData?.vehicleYear} ${carData?.brandName} ${carData?.modelName} (${carData?.vin})`}</Text>
            </View>

            <Image
              style={styles.listCarImage}
              source={{
                uri:
                  carData && getCarImages(carData.images).length > 0
                    ? getCarImages(carData.images)[0].url
                    : '',
              }}
              contentFit="contain"
            />

            <View style={styles.listCarStepsContainer}>
              <View style={styles.listCarStep}>
                <View
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 12,
                    backgroundColor:
                      theme.colors.progressBar.active.backgroundColor,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.inverse }}
                  >
                    1
                  </Text>
                </View>

                <View style={{ flexShrink: 1 }}>
                  <Text type="bodyRegular">Add location & car details</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Where your car is located and a few basic details
                  </Text>
                </View>
              </View>

              <View style={styles.listCarStep}>
                <View
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 12,
                    backgroundColor:
                      theme.colors.progressBar.active.backgroundColor,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.inverse }}
                  >
                    2
                  </Text>
                </View>

                <View style={{ flexShrink: 1 }}>
                  <Text type="bodyRegular">Set availability & pricing</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Choose your availability, set a rate, or let Eon price it
                    for you
                  </Text>
                </View>
              </View>

              <View style={styles.listCarStep}>
                <View
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 12,
                    backgroundColor:
                      theme.colors.progressBar.active.backgroundColor,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.inverse }}
                  >
                    3
                  </Text>
                </View>

                <View style={{ flexShrink: 1 }}>
                  <Text type="bodyRegular">Review & publish</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Confirm everything looks right, then list your car
                  </Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.listCarButtonsContainer}>
            <Button
              text="Next"
              type="primary"
              onPress={() => {
                if (locationsData && locationsData.length > 0) {
                  setShowAddLocationFormByCar((prev) => ({
                    ...prev,
                    [carScopeId]: false,
                  }));
                }

                setCurrentStep(Step.ADD_A_LOCATION);
              }}
              style={styles.listCarButton}
            />

            <Button
              text="Back to Cars"
              type="ghost"
              onPress={() => {
                router.navigate('/partner/cars');
              }}
              style={styles.listCarButton}
            />
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
      }}
    >
      {Platform.OS === 'web' && isDesktop() ? (
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
      ) : (
        <Stack.Screen
          options={{
            title: 'Connected Cars',
            headerTitle: selectedStep?.title,
            headerLeft: () => (
              <BackButton
                onPress={() => {
                  if (currentStep === Step.SUBSCRIPTION) {
                    setCurrentStep(Step.AVAILABILITY);
                    return;
                  }

                  if (currentStep === Step.AVAILABILITY) {
                    setCurrentStep(Step.PRICING);
                    return;
                  }

                  if (currentStep === Step.PRICING) {
                    setCurrentStep(Step.FILL_OUT_MISSING_DETAILS);
                    return;
                  }

                  if (currentStep === Step.FILL_OUT_MISSING_DETAILS) {
                    setCurrentStep(Step.ADD_A_LOCATION);
                    return;
                  }

                  if (currentStep === Step.ADD_A_LOCATION) {
                    setCurrentStep(Step.CHOOSE_CAR);
                    return;
                  }

                  router.navigate('/partner/cars');
                }}
              />
            ),
            headerShown: true,
            headerStyle: {
              backgroundColor: theme.colors.background_1,
            },
            headerTintColor: theme.colors.text.primary,
            headerShadowVisible: false,
            headerTitleAlign: 'center',
          }}
        />
      )}

      <View style={styles.outsideContainer}>
        <View style={styles.nav}>
          <NavigationButton
            type={NavigationButtonTypes.back_with_text}
            onPress={() => {
              if (currentStep === Step.SUBSCRIPTION) {
                setCurrentStep(Step.AVAILABILITY);
                return;
              }

              if (currentStep === Step.AVAILABILITY) {
                setCurrentStep(Step.PRICING);
                return;
              }

              if (currentStep === Step.PRICING) {
                setCurrentStep(Step.FILL_OUT_MISSING_DETAILS);
                return;
              }

              if (currentStep === Step.FILL_OUT_MISSING_DETAILS) {
                setCurrentStep(Step.ADD_A_LOCATION);
                return;
              }

              if (currentStep === Step.ADD_A_LOCATION) {
                setCurrentStep(Step.CHOOSE_CAR);
                return;
              }

              router.navigate('/partner/cars');
            }}
          />

          <View style={{ gap: 4 }}>
            <Text type="title1">{selectedStep?.title}</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              {selectedStep?.caption}
            </Text>
          </View>
        </View>

        <ProgressIndicator currentStep={currentStep} numSteps={7} />

        <View style={styles.container}>
          {[Step.CHOOSE_CAR].includes(currentStep) ? (
            <View style={styles.inputContainer}>
              <Input
                placeholder="Search for name, VIN, or license"
                value={filterText}
                onChangeText={(t: string) => setFilterText(t)}
                leftIcon={
                  <Icon
                    type="naked"
                    icon={<Search />}
                    fillColor={theme.colors.icon.color}
                    width={22}
                    height={22}
                  />
                }
              />
            </View>
          ) : null}

          <View style={{ flex: 1, display: 'flex', minHeight: 0, gap: 16 }}>
            <KeyboardAwareScrollView
              ref={scrollRef}
              style={{ flex: 1, minHeight: 0 }}
              contentContainerStyle={{ paddingBottom: 50 }}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="always"
            >
              {renderBody()}
            </KeyboardAwareScrollView>

            <View style={{ height: 50 }}>
              <Button
                text={currentStep === Step.SUBSCRIPTION ? 'List' : 'Next'}
                type="primary"
                onPress={handleNext}
                style={styles.button}
                disabled={
                  currentStep === Step.CHOOSE_CAR
                    ? selectedCar === null
                    : currentStep === Step.ADD_A_LOCATION
                    ? (showAddLocationForm && !addLocationFormValid) ||
                      (!showAddLocationForm && !pickupLocation)
                    : currentStep === Step.FILL_OUT_MISSING_DETAILS
                    ? !carSetupFormValid
                    : currentStep === Step.PRICING
                    ? !enabledFixedRate && minRate === 0
                    : currentStep === Step.AVAILABILITY
                    ? !availabilityFormValid
                    : currentStep === Step.SUBSCRIPTION
                    ? isUpdatingCar
                    : true
                }
                loading={isUpdatingCar}
              />
            </View>
          </View>
        </View>
      </View>

      <WhyGloveboxPinModal
        visible={showWhyGloveboxPinModal}
        onClose={() => {
          setShowWhyGloveboxPinModal(false);
        }}
      />

      <HowAutomatedPricingWorksModal
        visible={showHowAutomatedPricingWorksModal}
        onClose={() => {
          setShowHowAutomatedPricingWorksModal(false);
        }}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  outsideContainer: {
    flex: 1,
    paddingTop: {
      xs: 0,
      md: 56,
    },
    paddingLeft: {
      xs: 0,
      md: 56,
    },
    paddingRight: {
      xs: 0,
      md: 56,
    },
    paddingBottom: {
      xs: 0,
      md: 56,
    },
    gap: {
      xs: 0,
      md: 32,
    },
    width: '100%',
  },
  container: {
    flex: 1,
    paddingTop: {
      xs: 24,
      md: 0,
    },
    paddingLeft: {
      xs: 16,
      md: 0,
    },
    paddingRight: {
      xs: 16,
      md: 0,
    },
    paddingBottom: {
      xs: 24,
      md: 0,
    },
    gap: {
      xs: 16,
      md: 16,
    },
    width: '100%',
  },
  body: {
    flex: 1,
    gap: {
      xs: 56,
      md: 32,
    },
  },
  addCarsMobile: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    position: {
      xs: 'absolute',
    },
    bottom: {
      xs: 0,
    },
    flexDirection: 'row',
  },
  inputContainer: {
    width: {
      xs: '100%',
      md: 400,
    },
  },
  button: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
  carDetailsFormContainer: {
    gap: 16,
    flexDirection: {
      xs: 'column',
      md: 'column',
    },
    flexWrap: {
      xs: 'nowrap',
      md: 'wrap',
    },
    width: '100%',
  },
  carDetailsForm: {
    flex: 1,
    minWidth: {
      xs: '100%',
      md: 450,
    },
  },
  pickupLocationDropdown: {
    width: {
      xs: '100%',
      md: 450,
    },
  },
  gloveboxPinButton: {
    paddingLeft: 0,
    paddingRight: 0,
    width: {
      xs: '100%',
      md: 360,
    },
    justifyContent: {
      md: 'flex-start',
    },
  },
  pricingContainer: {
    gap: 32,
    maxWidth: {
      xs: '100%',
      md: 700,
    },
  },
  subscriptionContainer: {
    gap: 24,
    maxWidth: {
      xs: '100%',
      md: 700,
    },
  },
  dailyRateInputContainer: {
    gap: 8,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      md: 'center',
    },
    width: {
      md: '80%',
    },
  },
  listCarScrollView: {
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 72,
      md: 56,
    },
    paddingBottom: {
      xs: 56,
      md: 56,
    },
    flex: 1,
  },
  listCarScrollViewContentContainer: {
    gap: 24,
    flexGrow: 1,
    alignItems: {
      md: 'center',
    },
    justifyContent: 'center',
  },
  listCarImage: {
    width: '100%',
    height: {
      xs: 175,
      md: 275,
    },
  },
  listCarStepsContainer: {
    gap: 16,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    justifyContent: {
      md: 'center',
    },
    alignSelf: 'stretch',
  },
  listCarStep: {
    flexDirection: {
      xs: 'row',
      md: 'column',
    },
    gap: 16,
    maxWidth: {
      md: 250,
    },
  },
  listCarButtonsContainer: {
    width: '100%',
    gap: 8,
    flexDirection: {
      xs: 'column',
      md: 'row-reverse',
    },
    position: {
      xs: 'relative',
      md: 'absolute',
    },
    bottom: {
      md: 0,
    },
    right: {
      md: 100,
    },
  },
  listCarButton: {
    width: {
      xs: '100%',
      md: 200,
    },
  },
}));
